import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  EMPTY_CUSTOMER_CACHE,
  getCustomerCache,
  subscribeToCustomerCache,
} from '../api/customers';
import type { CustomerCache, CustomerCacheEntry } from '../types/customer';
import { useAuth } from './AuthContext';

interface CustomerCacheContextType {
  // Cache state
  cacheData: CustomerCache;
  loading: boolean;
  error: string | null;

  // Helper functions
  refreshCache: () => void;
  searchCustomers: (searchTerm: string) => CustomerCacheEntry[];
}

const CustomerCacheContext = createContext<
  CustomerCacheContextType | undefined
>(undefined);

// Helper to sort customers alphabetically by name
const sortCustomersByName = (
  customers: CustomerCacheEntry[]
): CustomerCacheEntry[] =>
  [...customers].sort((a, b) => a.name.localeCompare(b.name));

export const CustomerCacheProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { company, hasPermission } = useAuth();
  const canViewCustomers = hasPermission('canViewCustomers');
  const [cacheData, setCacheData] =
    useState<CustomerCache>(EMPTY_CUSTOMER_CACHE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // companies/{companyId}/cache/customers is admin-only per firestore.rules -
  // skip the subscription entirely for non-admins rather than let it fail
  // permission-denied on every page load.
  useEffect(() => {
    if (!company?.id || !canViewCustomers) {
      setCacheData(EMPTY_CUSTOMER_CACHE);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    const unsubscribe = subscribeToCustomerCache(
      company.id,
      (data) => {
        setCacheData({
          ...data,
          customersList: sortCustomersByName(data.customersList),
        });
        setLoading(false);
        setError(null);
      },
      () => {
        setError('Failed to load customers');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [company?.id, canViewCustomers]);

  // Search customers using cache (cacheData is always set at API/context level)
  const searchCustomers = useCallback(
    (searchTerm: string): CustomerCacheEntry[] => {
      const searchLower = searchTerm.toLowerCase();
      return cacheData.customersList.filter(
        (customer) =>
          customer.name.toLowerCase().includes(searchLower) ||
          (customer.email?.toLowerCase().includes(searchLower) ?? false) ||
          (customer.phone?.toLowerCase().includes(searchLower) ?? false) ||
          (customer.address?.toLowerCase().includes(searchLower) ?? false)
      );
    },
    [cacheData]
  );

  const refreshCache = useCallback(() => {
    if (!company?.id || !canViewCustomers) return;

    setLoading(true);
    getCustomerCache(company.id)
      .then((data) => {
        setCacheData({
          ...data,
          customersList: sortCustomersByName(data.customersList),
        });
        setError(null);
      })
      .catch((err) => {
        console.error('Error refreshing customer cache:', err);
        setError('Failed to refresh customers');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [company?.id, canViewCustomers]);

  const value: CustomerCacheContextType = {
    cacheData,
    loading,
    error,
    searchCustomers,
    refreshCache,
  };

  return (
    <CustomerCacheContext.Provider value={value}>
      {children}
    </CustomerCacheContext.Provider>
  );
};

export const useCustomerCacheContext = (): CustomerCacheContextType => {
  const context = useContext(CustomerCacheContext);
  if (!context) {
    throw new Error(
      'useCustomerCacheContext must be used within a CustomerCacheProvider'
    );
  }
  return context;
};
